import type { ParseFlowAction, ParseFlowOrigin } from '@/types/parse-flow-file';
import type { OrgManageTabId } from '@/lib/org-manage-nav';
import type { CanvasRouteContext } from '@/types/canvas';
import { getProjectCanvasPath as buildProjectCanvasPath } from '@/lib/canvas/canvas-routing';

/**
 * 路由常量
 */
export const ROUTES = {
  // 公开页面
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  ACCOUNT_APPEAL: '/account-appeal',
  PUBLIC_PROJECT_SHARE: '/share/p/:token',
  PUBLIC_FACE_VERIFICATION_RESULT: '/h5/face-verification/:sessionId/result',

  // 项目管理（个人上下文）
  WORKSPACE: '/workspace',
  WORKSPACE_FOLDER: '/workspace/f/:folderId',
  COLLAB: '/collab',
  COLLAB_FOLDER: '/collab/f/:folderId',

  // 项目管理（org 上下文，:orgId 区分于个人 f/ 前缀）
  WORKSPACE_ORG: '/workspace/:orgId',
  WORKSPACE_ORG_FOLDER: '/workspace/:orgId/:folderId',
  COLLAB_ORG: '/collab/:orgId',
  COLLAB_ORG_FOLDER: '/collab/:orgId/:folderId',
  TRASH_ORG: '/trash/:orgId',

  // 我分享的（上下文化路由，与 /workspace、/collab 对齐）
  // RFC-004 / temp-plan §1-2：URL 是 active org 的 source of truth
  //   - /shared         → 个人上下文（个人项目分享）
  //   - /shared/:orgId  → 组织上下文（该组织内部项目分享）
  SHARED: '/shared',
  SHARED_ORG: '/shared/:orgId',

  // AI 工具箱（页面化）
  AI_TOOLBOX: '/ai-toolbox',
  AI_TOOLBOX_TOOL: '/ai-toolbox/:tool',
  AI_TOOLBOX_ORG: '/ai-toolbox/:orgId',
  AI_TOOLBOX_ORG_TOOL: '/ai-toolbox/:orgId/:tool',
  REALTIME_ASR_DEMO: '/demo/realtime-asr',

  PROJECT: '/project/:projectId',
  PROJECT_CANVAS: '/project/:projectId/canvas',
  PROJECT_ORG_PRODUCER_SETUP: '/project/:projectId/org/producer-setup',
  PARSE_FLOW: '/project/parse-flow',
  AI_WORKSPACE: '/project/ai-workspace',
  PARSE_FLOW_LEGACY: '/project/:projectId/parse-flow',
  TRASH: '/trash',

  // 个人账户
  ACCOUNT_ROOT: '/account',
  ACCOUNT: '/account/:tab',
  ACCOUNT_PROFILE: '/account/profile',
  ACCOUNT_TRANSACTIONS: '/account/transactions',
  ACCOUNT_CONSUMPTION: '/account/consumption',

  // 设置
  SETTINGS: '/settings',
  PROFILE: '/settings/profile',
  PREFERENCES: '/settings/preferences',

  // 组织管理
  ORG_SETTINGS: '/org/:orgId',
  ORG_EXTERNAL_MEMBER_REVIEWS: '/org/:orgId/external-member-invitations',
  CERTIFICATION: '/certification',
  INVITE: '/invite/:token',
  TEMP_MEMBER_INVITE: '/temp-member-invitations/:token',

  // 通知中心
  NOTIFICATIONS: '/notifications',
  MY_ORGS: '/my-orgs',

  // 其他
  NOT_FOUND: '/404',
} as const;

export const getOrgSettingsPath = (orgId: string) => `/org/${orgId}`;
export const getOrgExternalMemberReviewsPath = (orgId: string) =>
  `/org/${orgId}/external-member-invitations`;
export const getOrgSettingsTabPath = (orgId: string, tab: OrgManageTabId) =>
  `/org/${orgId}?tab=${tab}`;

// Org-aware 工作台路径辅助函数
// orgId 为 null/undefined 时返回个人路径，有值时返回 org 路径
export const getWorkspacePath = (orgId?: string | null) =>
  orgId ? `/workspace/${orgId}` : '/workspace';

export const getCollabPath = (orgId?: string | null) => (orgId ? `/collab/${orgId}` : '/collab');

export const getTrashPath = (orgId?: string | null) => (orgId ? `/trash/${orgId}` : '/trash');
export type WorkspaceProjectAction = 'pendingCredit' | 'producerSetup' | 'memberSetup';
export const getWorkspaceProjectActionPath = (
  orgId: string,
  action: WorkspaceProjectAction,
  projectId: string,
  folderId?: string | null
) => {
  const params = new URLSearchParams({ action, projectId });
  const basePath = folderId ? getWorkspaceFolderPath(folderId, orgId) : getWorkspacePath(orgId);
  return `${basePath}?${params.toString()}`;
};
export const getAccountTabPath = (tab: 'profile' | 'transactions' | 'consumption') =>
  `/account/${tab}`;
/**
 * 「我分享的」路径（上下文化）。
 * @param orgId null/undefined → 个人上下文 /shared；有值 → 组织上下文 /shared/:orgId。
 */
export const getSharedPath = (orgId?: string | null) => (orgId ? `/shared/${orgId}` : '/shared');
export const getMyOrgsPath = () => '/my-orgs';
export type AIToolboxRouteTool = 'digital-human-verification' | 'prompt-library' | 'skill-library';
export const DEFAULT_AI_TOOLBOX_TOOL: AIToolboxRouteTool = 'digital-human-verification';
export const getAIToolboxPath = (orgId?: string | null, tool?: AIToolboxRouteTool) => {
  if (orgId) {
    return `/ai-toolbox/${orgId}${tool ? `/${tool}` : ''}`;
  }
  return tool ? `/ai-toolbox/${tool}` : '/ai-toolbox';
};

export const getWorkspaceFolderPath = (folderId: string, orgId?: string | null) =>
  orgId ? `/workspace/${orgId}/${folderId}` : `/workspace/f/${folderId}`;

export const getCollabFolderPath = (folderId: string, orgId?: string | null) =>
  orgId ? `/collab/${orgId}/${folderId}` : `/collab/f/${folderId}`;

// 路由辅助函数
export const getProjectPath = (projectId: string) => `/project/${projectId}`;
export const getProjectCanvasPath = (projectId: string, context: CanvasRouteContext) =>
  buildProjectCanvasPath(projectId, context);
export const getOrgProducerSetupPath = (projectId: string) =>
  `/project/${projectId}/org/producer-setup`;
export const getProjectDirectorSetupPath = (projectId: string) =>
  `${ROUTES.PARSE_FLOW}?${new URLSearchParams({
    parentProjectId: projectId,
    origin: 'project-page',
  }).toString()}`;
export const getInvitePath = (token: string) => `/invite/${token}`;
export const getTempMemberInvitePath = (token: string) => `/temp-member-invitations/${token}`;

export const getParseFlowPath = (
  parentProjectId: string,
  options?: {
    projectId?: string;
    screenplayId?: string;
    storyboardId?: string;
    anchorStoryboardId?: string;
    direction?: 'before' | 'after';
    origin?: ParseFlowOrigin;
    action?: ParseFlowAction;
    returnTo?: string;
  }
): string => {
  const params = new URLSearchParams({ parentProjectId });
  if (options?.projectId) params.set('projectId', options.projectId);
  if (options?.screenplayId) params.set('screenplayId', options.screenplayId);
  if (options?.storyboardId) params.set('storyboardId', options.storyboardId);
  if (options?.anchorStoryboardId) params.set('anchorStoryboardId', options.anchorStoryboardId);
  if (options?.direction) params.set('direction', options.direction);
  if (options?.origin) params.set('origin', options.origin);
  if (options?.action) params.set('action', options.action);
  if (options?.returnTo) params.set('returnTo', options.returnTo);
  return `${ROUTES.PARSE_FLOW}?${params.toString()}`;
};
